/* ==========================================================================
   Stacks — product 3D viewer
   Reads the STL/OBJ/3MF attached to the product (metafield custom.model_file) and shows it
   in a soft studio: drag to turn, pinch / wheel to zoom. three.js loads only once the viewer
   is near the screen, so pages without a model — or visitors who never scroll to it — pay nothing.
   ========================================================================== */
const roots = [...document.querySelectorAll('[data-model3d]')];
if (roots.length) {
  const io = new IntersectionObserver((entries) => {
    entries.forEach(en => { if (en.isIntersecting) { io.unobserve(en.target); boot(en.target); } });
  }, { rootMargin: '200px' });
  roots.forEach(r => io.observe(r));
}

async function boot(root) {
  const setState = (s) => { root.dataset.state = s; };
  const bar = root.querySelector('[data-m3d-progress]');
  try {
    const THREE = await import('three');
    const { OrbitControls } = await import('three/addons/controls/OrbitControls.js');
    const { RoomEnvironment } = await import('three/addons/environments/RoomEnvironment.js');
    const url = root.dataset.src;
    const ext = (root.dataset.ext || url.split('?')[0].split('.').pop()).toLowerCase();

    // download with progress so a 10 MB file doesn't look frozen
    const res = await fetch(url);
    if (!res.ok) throw new Error('download');
    const total = +res.headers.get('content-length') || 0;
    const reader = res.body.getReader(); const chunks = []; let got = 0;
    for (;;) { const { done, value } = await reader.read(); if (done) break; chunks.push(value); got += value.length; if (total && bar) bar.style.width = `${Math.round(got / total * 100)}%`; }
    const buf = await new Blob(chunks).arrayBuffer();

    let geos;
    if (ext === 'stl') { const { STLLoader } = await import('three/addons/loaders/STLLoader.js'); geos = [new STLLoader().parse(buf)]; }
    else if (ext === 'obj') { const { OBJLoader } = await import('three/addons/loaders/OBJLoader.js'); geos = collect(new OBJLoader().parse(new TextDecoder().decode(buf))); }
    else if (ext === '3mf') { const { ThreeMFLoader } = await import('three/addons/loaders/3MFLoader.js'); geos = collect(new ThreeMFLoader().parse(buf)); }
    else throw new Error('type');

    function collect(obj) { const out = []; obj.updateMatrixWorld(true); obj.traverse(c => { if (c.isMesh) { const g = c.geometry.clone(); g.applyMatrix4(c.matrixWorld); out.push(g); } }); return out; }

    // ---------- renderer + studio ----------
    const canvasWrap = root.querySelector('[data-m3d-canvas]');
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.shadowMap.enabled = true; renderer.shadowMap.type = THREE.VSMShadowMap;
    canvasWrap.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environmentIntensity = .6;

    const color = new THREE.Color(root.dataset.color || '#E8E5DA');
    const mat = new THREE.MeshStandardMaterial({ color, roughness: .52, metalness: .02 });
    followColourOption(root, mat);
    // faint FDM layer lines, so it reads as a printed part rather than a CAD render
    mat.onBeforeCompile = (sh) => {
      sh.vertexShader = sh.vertexShader.replace('#include <common>', '#include <common>\nvarying float vWY;')
        .replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\nvWY = (modelMatrix * vec4(transformed, 1.0)).y;');
      sh.fragmentShader = sh.fragmentShader.replace('#include <common>', '#include <common>\nvarying float vWY;\nuniform float uLayer;')
        .replace('#include <dithering_fragment>', '#include <dithering_fragment>\nfloat ph = fract(vWY / uLayer);\nfloat lw = fwidth(vWY) / uLayer;\nfloat lines = mix(.94, 1.01, smoothstep(0., .3, ph) * smoothstep(1., .7, ph));\ngl_FragColor.rgb *= mix(1., lines, 1. - smoothstep(.12, .45, lw));');
      sh.uniforms.uLayer = { value: .7 };   // lines fade wherever they'd shimmer (fwidth) instead of turning to moiré
    };

    const group = new THREE.Group();
    geos.forEach(g => { g.computeVertexNormals(); const m = new THREE.Mesh(g, mat); m.castShadow = true; m.receiveShadow = true; group.add(m); });
    group.rotation.x = -Math.PI / 2;                       // print files are Z-up
    scene.add(group);

    // centre on the floor; scale so layer lines stay proportional regardless of file units
    let box = new THREE.Box3().setFromObject(group);
    const size = box.getSize(new THREE.Vector3());
    const R = Math.max(size.x, size.y, size.z) || 1;
    const k = 100 / R; group.scale.setScalar(k);
    box = new THREE.Box3().setFromObject(group);
    const c = box.getCenter(new THREE.Vector3());
    group.position.x -= c.x; group.position.z -= c.z; group.position.y -= box.min.y;
    box = new THREE.Box3().setFromObject(group);
    const sphere = box.getBoundingSphere(new THREE.Sphere());

    const floor = new THREE.Mesh(new THREE.CircleGeometry(400, 64), new THREE.ShadowMaterial({ opacity: .24 }));
    floor.rotation.x = -Math.PI / 2; floor.receiveShadow = true; scene.add(floor);

    const key = new THREE.DirectionalLight(0xffffff, 2.2);
    key.position.set(-80, 180, 110); key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024); key.shadow.radius = 12; key.shadow.blurSamples = 16; key.shadow.bias = -.0005;
    Object.assign(key.shadow.camera, { left: -130, right: 130, top: 130, bottom: -130, near: 1, far: 500 });
    scene.add(key);
    const rim = new THREE.DirectionalLight(0xffffff, .9); rim.position.set(120, 80, -120); scene.add(rim);
    scene.add(new THREE.HemisphereLight(0xffffff, 0xd8d3c4, .5));

    const camera = new THREE.PerspectiveCamera(32, 1, .5, 3000);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true; controls.dampingFactor = .08;
    controls.enablePan = false;
    controls.autoRotate = !matchMedia('(prefers-reduced-motion: reduce)').matches; controls.autoRotateSpeed = 1.1;
    controls.maxPolarAngle = Math.PI * .52;                 // don't dive under the floor
    const fitDist = sphere.radius / Math.sin(THREE.MathUtils.degToRad(16)) * 1.12;
    controls.minDistance = fitDist * .35; controls.maxDistance = fitDist * 2.2;

    const frame = (animate) => {
      const dir = new THREE.Vector3(1, .62, 1.35).normalize();
      const to = sphere.center.clone().addScaledVector(dir, fitDist);
      if (!animate) { camera.position.copy(to); controls.target.copy(sphere.center); controls.update(); return; }
      const from = camera.position.clone(), t0 = performance.now();
      const step = (now) => { const t = Math.min(1, (now - t0) / 600), e = 1 - Math.pow(1 - t, 3); camera.position.lerpVectors(from, to, e); controls.target.lerp(sphere.center, e); if (t < 1) requestAnimationFrame(step); };
      requestAnimationFrame(step);
    };
    frame(false);

    const resize = () => {
      const { width, height } = canvasWrap.getBoundingClientRect();
      if (!width || !height) return;
      renderer.setSize(width, height, false);
      camera.aspect = width / height; camera.updateProjectionMatrix();
    };
    new ResizeObserver(resize).observe(canvasWrap); resize();

    // render only while on screen and visible
    let onScreen = true;
    new IntersectionObserver(([en]) => { onScreen = en.isIntersecting; }).observe(root);
    const loop = () => { if (onScreen && !root.closest('[hidden]')) { controls.update(); renderer.render(scene, camera); } requestAnimationFrame(loop); };
    loop();

    // stop turning once the customer takes over; tell the gallery swipe to stand down
    const stopSpin = () => { controls.autoRotate = false; root.classList.add('is-touched'); };
    renderer.domElement.addEventListener('pointerdown', stopSpin);
    renderer.domElement.addEventListener('wheel', stopSpin, { passive: true });
    ['touchstart', 'touchend'].forEach(ev => root.addEventListener(ev, e => e.stopPropagation(), { passive: true }));

    const zoom = (f) => { stopSpin(); const d = camera.position.clone().sub(controls.target); const len = THREE.MathUtils.clamp(d.length() * f, controls.minDistance, controls.maxDistance); camera.position.copy(controls.target).addScaledVector(d.normalize(), len); };
    root.addEventListener('click', (e) => {
      const b = e.target.closest('[data-m3d]'); if (!b) return;
      const a = b.dataset.m3d;
      if (a === 'in') zoom(.8);
      else if (a === 'out') zoom(1.25);
      else if (a === 'reset') { controls.autoRotate = true; root.classList.remove('is-touched'); frame(true); }
      else if (a === 'full') { document.fullscreenElement ? document.exitFullscreen() : root.requestFullscreen?.().catch(() => {}); }
    });

    const dims = root.querySelector('[data-m3d-dims]');
    if (dims && root.dataset.showDims !== 'false') dims.textContent = `${Math.round(size.x)} × ${Math.round(size.z)} × ${Math.round(size.y)} mm`;   // W × D × H (scene Y is up)
    setState('ready');
  } catch (err) {
    console.warn('[3d viewer]', err);
    setState('error');
  }
}

/* The model takes the colour the shopper picks. Names come from the print palette (quote settings,
   "Black:#16171A" per line) plus their Arabic names, since option values are translated on /ar. */
const AR_NAMES = { black: 'أسود', white: 'أبيض', grey: 'رمادي', gray: 'رمادي', red: 'أحمر', blue: 'أزرق', green: 'أخضر', yellow: 'أصفر', orange: 'برتقالي' };
const FALLBACK = ['Black:#16171A', 'White:#F4F3EF', 'Grey:#8E9096', 'Red:#D8342C', 'Blue:#2C5BD8', 'Green:#2E9E5B', 'Yellow:#F2C230', 'Orange:#F07A2A'].join('\n');
function followColourOption(root, mat) {
  const palette = {};
  for (const line of (root.dataset.palette || FALLBACK).split(/\r?\n/)) {
    const [name, hex] = line.split(':').map((x) => (x || '').trim());
    if (!name || !/^#[0-9a-f]{6}$/i.test(hex)) continue;
    palette[name.toLowerCase()] = hex;
    if (AR_NAMES[name.toLowerCase()]) palette[AR_NAMES[name.toLowerCase()]] = hex;
  }
  const form = root.closest('[data-product]') || document;
  const fieldset = () => [...form.querySelectorAll('fieldset[data-option]')]
    .find((f) => /colou?r|لون/i.test(f.querySelector('legend')?.textContent || ''));
  const apply = () => {
    const picked = fieldset()?.querySelector('input:checked');
    const hex = picked && palette[picked.value.trim().toLowerCase()];
    if (hex) mat.color.set(hex);
  };
  apply();
  form.addEventListener('change', (e) => { if (e.target.closest('fieldset[data-option]')) apply(); });
}
